import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import { getEnumValues } from '@pushly/models'
import {
    AllFilterRecordMap,
    BlueConicPQueryDocument,
    CustomFilter,
    dateFilterBlueConicType,
    Filter,
    filterIsAnyOfOrAllOf,
    filterIsCustom,
    filterIsDateType,
    filterIsFreeText,
    FilterMap,
    freeTextFilterBlueConicType,
    isAnyOrAllOfFilterBlueConicType,
    RelativeDateFilter,
    SingleComparator,
    SpecificDateFilter,
} from './types'
import { BlueConicFilterType, ConditionOperatorType, FilterType, ProductsFilters } from './enums'
import { toArray } from 'antd/es/form/util'

export const productFilterValues: string[] = getEnumValues(ProductsFilters)

const excludedConditions = [ConditionOperatorType.NOT_EQUALS, ConditionOperatorType.NEQ_ANY]

export function extractFilterRecordMapFromPQuery(filters?: Record<'and', any[]> | null): AllFilterRecordMap {
    filters ??= { and: [] }

    const result: AllFilterRecordMap = {
        included: new Map(),
        excluded: new Map(),
        products: [],
    }

    // Small local helpers to keep logic readable and reduce duplication
    const isExcluded = (cond: string | undefined) => excludedConditions.includes(cond as ConditionOperatorType)
    const ensureBucket = (obj: Record<string, string[]>, key: string) => (obj[key] ??= [])
    const getConditionKey = (obj: Record<string, any>) => {
        for (const k in obj) if (k !== 'meta') return k
        return undefined as unknown as string
    }

    const allOfValues: {
        included: {
            [BlueConicFilterType.CATEGORY]?: string[]
            [BlueConicFilterType.AUTHOR]?: string[]
            [BlueConicFilterType.SITENAME]?: string[]
        }
        excluded: {
            [BlueConicFilterType.CATEGORY]?: string[]
            [BlueConicFilterType.AUTHOR]?: string[]
            [BlueConicFilterType.SITENAME]?: string[]
        }
    } = { included: {}, excluded: {} }

    // Accumulator for custom property allOf collapsing (mirrors allOfValues for known types)
    const customAllOfValues: {
        included: Record<string, string[]>
        excluded: Record<string, string[]>
    } = { included: {}, excluded: {} }

    for (const filter of filters.and) {
        const propertyStr = Object.keys(filter)[0] // each value is a single key
        const propertyValue = filter[propertyStr]
        const condition = getConditionKey(propertyValue)
        const criteriaKey = isExcluded(condition) ? 'excluded' : 'included'

        if (productFilterValues.includes(propertyValue[condition])) {
            result.products.push(propertyValue[condition])
            continue
        }

        if (isAnyOrAllOfFilterBlueConicType(propertyStr)) {
            const isAny = Array.isArray(propertyValue[condition])

            if (!isAny) {
                const bucket = ensureBucket(
                    allOfValues[criteriaKey],
                    propertyStr as
                        | BlueConicFilterType.CATEGORY
                        | BlueConicFilterType.AUTHOR
                        | BlueConicFilterType.SITENAME,
                )
                bucket.push(...toArray(propertyValue[condition]))
                continue
            } else {
                result[criteriaKey].set(getRandId(), {
                    blueConicType: propertyStr as
                        | BlueConicFilterType.CATEGORY
                        | BlueConicFilterType.AUTHOR
                        | BlueConicFilterType.SITENAME,
                    filterType: FilterType.MATCHES_ANY_OF,
                    value: propertyValue[condition],
                })
                continue
            }
        } else if (freeTextFilterBlueConicType.includes(propertyStr)) {
            result[criteriaKey].set(getRandId(), {
                blueConicType: propertyStr as BlueConicFilterType.CUSTOM | BlueConicFilterType.PROPERTY,
                filterType: FilterType.FREE_TEXT,
                value: propertyValue[condition],
            })
            continue
        } else if (dateFilterBlueConicType.includes(propertyStr)) {
            const filterType =
                condition === ConditionOperatorType.EQUALS ? FilterType.SPECIFIC_DATE : FilterType.RELATIVE_DATE
            result[criteriaKey].set(getRandId(), {
                blueConicType: propertyStr as BlueConicFilterType.PUBLICATION_DATE,
                filterType,
                value: {
                    operator: condition,
                    value: propertyValue[condition],
                    meta: propertyValue.meta ?? {},
                },
            } as SpecificDateFilter | RelativeDateFilter)
        } else {
            // Custom properties
            const conditionValue = propertyValue[condition]
            const hasMeta = propertyValue.meta !== undefined

            if (
                [ConditionOperatorType.EQ_ANY, ConditionOperatorType.NEQ_ANY].includes(
                    condition as ConditionOperatorType,
                )
            ) {
                // Array-based operators => MATCHES_ANY_OF (same as known types)
                result[criteriaKey].set(getRandId(), {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: propertyStr,
                    nestedFilter: {
                        filterType: FilterType.MATCHES_ANY_OF,
                        value: conditionValue,
                    },
                })
            } else if (hasMeta) {
                // Date-like filter with meta — distinguish relative vs specific
                const isRelative = propertyValue.meta?.qualifier && typeof conditionValue === 'number'

                result[criteriaKey].set(getRandId(), {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: propertyStr,
                    nestedFilter: isRelative
                        ? {
                              filterType: FilterType.RELATIVE_DATE,
                              value: {
                                  operator: condition as ConditionOperatorType.LTE | ConditionOperatorType.GT,
                                  value: conditionValue,
                                  meta: propertyValue.meta,
                              },
                          }
                        : {
                              filterType: FilterType.SPECIFIC_DATE,
                              value: {
                                  operator: condition as SpecificDateFilter['value']['operator'],
                                  value: conditionValue,
                                  meta: propertyValue.meta,
                              },
                          },
                })
            } else if (
                typeof conditionValue === 'string' &&
                [ConditionOperatorType.EQUALS, ConditionOperatorType.NOT_EQUALS].includes(
                    condition as ConditionOperatorType,
                )
            ) {
                // Scalar eq/neq string => collapse into allOf accumulator (mirrors known types)
                const bucket = ensureBucket(customAllOfValues[criteriaKey], propertyStr)
                bucket.push(conditionValue)
            } else if (typeof conditionValue === 'number') {
                result[criteriaKey].set(getRandId(), {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: propertyStr,
                    nestedFilter: {
                        filterType: FilterType.NUMERICAL,
                        value: {
                            operator: condition as SingleComparator['operator'],
                            value: conditionValue,
                        },
                    },
                })
            } else if (typeof conditionValue === 'string') {
                // Non eq/neq string operators (range comparisons on date-like strings, etc.)
                result[criteriaKey].set(getRandId(), {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: propertyStr,
                    nestedFilter: {
                        filterType: FilterType.FREE_TEXT,
                        value: conditionValue,
                    },
                })
            } else {
                result[criteriaKey].set(getRandId(), {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: propertyStr,
                    nestedFilter: { filterType: FilterType.FREE_TEXT },
                })
            }
        }
    }

    ;(['included', 'excluded'] as const).forEach((key) => {
        for (const [blueConicType, value] of Object.entries(allOfValues[key])) {
            result[key].set(getRandId(), {
                blueConicType: blueConicType as
                    | BlueConicFilterType.CATEGORY
                    | BlueConicFilterType.AUTHOR
                    | BlueConicFilterType.SITENAME,
                filterType: FilterType.MATCHES_ALL_OF,
                value,
            })
        }

        // Flush custom property allOf accumulator (mirrors known types above)
        for (const [property, value] of Object.entries(customAllOfValues[key])) {
            result[key].set(getRandId(), {
                blueConicType: BlueConicFilterType.CUSTOM,
                filterType: FilterType.CUSTOM,
                property,
                nestedFilter:
                    value.length === 1
                        ? { filterType: FilterType.FREE_TEXT, value: value[0] }
                        : { filterType: FilterType.MATCHES_ALL_OF, value },
            })
        }
    })

    return result
}

function getPQueryConditionFromFilterType(filterType: FilterType, isExcluded: boolean): ConditionOperatorType {
    let condition

    switch (filterType) {
        case FilterType.FREE_TEXT:
        case FilterType.MATCHES_ALL_OF:
        case FilterType.SPECIFIC_DATE:
            condition = isExcluded ? ConditionOperatorType.NOT_EQUALS : ConditionOperatorType.EQUALS
            break
        case FilterType.MATCHES_ANY_OF:
            condition = isExcluded ? ConditionOperatorType.NEQ_ANY : ConditionOperatorType.EQ_ANY
            break
    }

    return condition
}

function getPQueryConditionFromConditionContext(
    condition: ConditionOperatorType,
    isExcluded: boolean,
): ConditionOperatorType {
    let result

    switch (condition) {
        case ConditionOperatorType.EQUALS:
            result = isExcluded ? ConditionOperatorType.NOT_EQUALS : ConditionOperatorType.EQUALS
            break
        case ConditionOperatorType.LT:
            result = isExcluded ? ConditionOperatorType.GT : ConditionOperatorType.LT
            break
        case ConditionOperatorType.LTE:
            result = isExcluded ? ConditionOperatorType.GTE : ConditionOperatorType.LTE
            break
        case ConditionOperatorType.GT:
            result = isExcluded ? ConditionOperatorType.LT : ConditionOperatorType.GT
            break
        case ConditionOperatorType.GTE:
            result = isExcluded ? ConditionOperatorType.LTE : ConditionOperatorType.GTE
            break
        case ConditionOperatorType.EQ_ANY:
            result = isExcluded ? ConditionOperatorType.NEQ_ANY : ConditionOperatorType.EQ_ANY
            break
    }

    return result
}

export function parsePQueryFromFilterRecordMap(recordMap: AllFilterRecordMap): BlueConicPQueryDocument | null {
    const document: BlueConicPQueryDocument = { and: [] }

    const pushRule = (key: string, operator: ConditionOperatorType, value: any, meta?: Record<string, any>) => {
        const payload: any = { [operator]: value }
        if (meta !== undefined) {
            payload.meta = meta
        }

        document.and.push({ [key]: payload })
    }

    for (const productFilter of recordMap.products) {
        pushRule('product_filter', ConditionOperatorType.EQUALS, productFilter)
    }

    const buildAndPushPQueryRule = (filterMap: FilterMap, isExcluded: boolean) => {
        for (const [_key, filter] of filterMap) {
            const filterType = filter.filterType
            const blueConicType = filter.blueConicType

            if (filterIsAnyOfOrAllOf(filter)) {
                if (filterType === FilterType.MATCHES_ALL_OF) {
                    for (const str of toArray(filter.value ?? [])) {
                        pushRule(blueConicType, getPQueryConditionFromFilterType(filterType, isExcluded), str)
                    }
                } else {
                    pushRule(blueConicType, getPQueryConditionFromFilterType(filterType, isExcluded), filter.value)
                }
            } else if (filterIsFreeText(filter)) {
                pushRule(blueConicType, getPQueryConditionFromFilterType(filterType, isExcluded), filter.value)
            } else if (filterIsDateType(filter)) {
                pushRule(
                    blueConicType,
                    getPQueryConditionFromConditionContext(filter.value.operator, isExcluded),
                    filter.value.value,
                    filter.value.meta,
                )
            } else if (filterIsCustom(filter)) {
                if (filter.nestedFilter.filterType === FilterType.MATCHES_ALL_OF) {
                    for (const str of toArray(filter.nestedFilter.value)) {
                        pushRule(
                            filter.property,
                            getPQueryConditionFromFilterType(filter.nestedFilter.filterType, isExcluded),
                            str,
                        )
                    }
                } else if (filter.nestedFilter.filterType === FilterType.NUMERICAL) {
                    pushRule(
                        filter.property,
                        getPQueryConditionFromConditionContext(filter.nestedFilter.value.operator, isExcluded),
                        filter.nestedFilter.value.value,
                    )
                } else if (filterIsDateType(filter.nestedFilter)) {
                    pushRule(
                        filter.property,
                        getPQueryConditionFromConditionContext(filter.nestedFilter.value.operator, isExcluded),
                        filter.nestedFilter.value.value,
                        filter.nestedFilter.value.meta,
                    )
                } else {
                    pushRule(
                        filter.property,
                        getPQueryConditionFromFilterType(filter.nestedFilter.filterType, isExcluded),
                        filter.nestedFilter.value,
                    )
                }
            }
        }
    }

    buildAndPushPQueryRule(recordMap.included, false)
    buildAndPushPQueryRule(recordMap.excluded, true)

    return document.and.length > 0 ? document : null
}

export function simpleDateFormat(date: Date) {
    const year = date.getFullYear()
    // Month is 0-indexed, so add 1 and pad with a leading zero if necessary
    const month = String(date.getMonth() + 1).padStart(2, '0')
    // Pad day with a leading zero if necessary
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest

    describe('extractFilterRecordMapFromPQuery', () => {
        it('returns empty maps/products when no filters provided', () => {
            const result = extractFilterRecordMapFromPQuery()
            expect(result).toEqual({
                included: new Map(),
                excluded: new Map(),
                products: [],
            })
        })

        it('collects product filters', () => {
            const filters = {
                and: [
                    { product_filter: { [ConditionOperatorType.EQUALS]: productFilterValues[0] } },
                    { product_filter: { [ConditionOperatorType.EQUALS]: productFilterValues[1] } },
                ],
            }

            const result = extractFilterRecordMapFromPQuery(filters)
            expect(result.products).toEqual([productFilterValues[0], productFilterValues[1]])
        })

        it('classifies included vs excluded based on operator', () => {
            const filters = {
                and: [
                    { [BlueConicFilterType.CATEGORY]: { [ConditionOperatorType.EQUALS]: 'foo' } },
                    { [BlueConicFilterType.AUTHOR]: { [ConditionOperatorType.NOT_EQUALS]: 'bar' } },
                ],
            }

            const result = extractFilterRecordMapFromPQuery(filters)

            expect(Array.from(result.included.values())).toEqual([
                {
                    blueConicType: BlueConicFilterType.CATEGORY,
                    filterType: FilterType.MATCHES_ALL_OF,
                    value: ['foo'],
                },
            ])

            expect(Array.from(result.excluded.values())).toEqual([
                {
                    blueConicType: BlueConicFilterType.AUTHOR,
                    filterType: FilterType.MATCHES_ALL_OF,
                    value: ['bar'],
                },
            ])
        })

        it('handles matches_any_of correctly', () => {
            const filters = {
                and: [
                    { [BlueConicFilterType.CATEGORY]: { [ConditionOperatorType.EQ_ANY]: ['cat1', 'cat2'] } },
                    { [BlueConicFilterType.CATEGORY]: { [ConditionOperatorType.EQ_ANY]: ['cat3', 'cat4'] } },
                ],
            }

            const result = extractFilterRecordMapFromPQuery(filters)

            expect(Array.from(result.included.values())).toEqual([
                {
                    blueConicType: BlueConicFilterType.CATEGORY,
                    filterType: FilterType.MATCHES_ANY_OF,
                    value: ['cat1', 'cat2'],
                },
                {
                    blueConicType: BlueConicFilterType.CATEGORY,
                    filterType: FilterType.MATCHES_ANY_OF,
                    value: ['cat3', 'cat4'],
                },
            ])
        })

        it('handles matches_all_of correctly (multiple equals entries collapsed in record map)', () => {
            const filters = {
                and: [
                    { [BlueConicFilterType.CATEGORY]: { [ConditionOperatorType.EQUALS]: 'cat1' } },
                    { [BlueConicFilterType.CATEGORY]: { [ConditionOperatorType.EQUALS]: 'cat2' } },
                ],
            }

            const result = extractFilterRecordMapFromPQuery(filters)

            expect(Array.from(result.included.values())).toEqual([
                {
                    blueConicType: BlueConicFilterType.CATEGORY,
                    filterType: FilterType.MATCHES_ALL_OF,
                    value: ['cat1', 'cat2'],
                },
            ])
        })

        it('handles specific date filter with meta', () => {
            const dateFilter = {
                [BlueConicFilterType.PUBLICATION_DATE]: {
                    [ConditionOperatorType.EQUALS]: '2025-08-19',
                    meta: { timezone: 'America/Chicago' },
                },
            }

            const result = extractFilterRecordMapFromPQuery({ and: [dateFilter] })

            const values = Array.from(result.included.values())
            expect(values).toHaveLength(1)
            expect(values[0]).toEqual({
                blueConicType: BlueConicFilterType.PUBLICATION_DATE,
                filterType: FilterType.SPECIFIC_DATE,
                value: {
                    operator: ConditionOperatorType.EQUALS,
                    value: '2025-08-19',
                    meta: { timezone: 'America/Chicago' },
                },
            })
        })

        it('handles relative date filter with meta', () => {
            const dateFilter = {
                [BlueConicFilterType.PUBLICATION_DATE]: {
                    [ConditionOperatorType.GT]: 7,
                    meta: { timezone: 'America/Chicago', qualifier: 'days' },
                },
            }

            const result = extractFilterRecordMapFromPQuery({ and: [dateFilter] })

            const values = Array.from(result.included.values())
            expect(values).toHaveLength(1)
            expect(values[0]).toEqual({
                blueConicType: BlueConicFilterType.PUBLICATION_DATE,
                filterType: FilterType.RELATIVE_DATE,
                value: {
                    operator: ConditionOperatorType.GT,
                    value: 7,
                    meta: { timezone: 'America/Chicago', qualifier: 'days' },
                },
            })
        })

        it('handles free text for PROPERTY correctly', () => {
            const filters = {
                and: [{ [BlueConicFilterType.PROPERTY]: { [ConditionOperatorType.EQUALS]: 'foo' } }],
            }

            const result = extractFilterRecordMapFromPQuery(filters)
            expect(Array.from(result.included.values())).toEqual([
                {
                    blueConicType: BlueConicFilterType.PROPERTY,
                    filterType: FilterType.FREE_TEXT,
                    value: 'foo',
                },
            ])
        })

        it('handles custom property nested filters: EQ_ANY as MATCHES_ANY_OF; string eq as collapsed allOf; number as NUMERICAL', () => {
            const filters = {
                and: [
                    { custom_prop_any: { [ConditionOperatorType.EQ_ANY]: ['a', 'b'] } },
                    { custom_prop_text: { [ConditionOperatorType.EQUALS]: 'abc' } },
                    { custom_prop_num: { [ConditionOperatorType.GTE]: 10 } },
                ],
            } as any

            const result = extractFilterRecordMapFromPQuery(filters)
            const values = Array.from(result.included.values())

            expect(values).toEqual(
                expect.arrayContaining([
                    {
                        blueConicType: BlueConicFilterType.CUSTOM,
                        filterType: FilterType.CUSTOM,
                        property: 'custom_prop_any',
                        nestedFilter: {
                            filterType: FilterType.MATCHES_ANY_OF,
                            value: ['a', 'b'],
                        },
                    },
                    {
                        blueConicType: BlueConicFilterType.CUSTOM,
                        filterType: FilterType.CUSTOM,
                        property: 'custom_prop_text',
                        nestedFilter: {
                            filterType: FilterType.FREE_TEXT,
                            value: 'abc',
                        },
                    },
                    {
                        blueConicType: BlueConicFilterType.CUSTOM,
                        filterType: FilterType.CUSTOM,
                        property: 'custom_prop_num',
                        nestedFilter: {
                            filterType: FilterType.NUMERICAL,
                            value: {
                                operator: ConditionOperatorType.GTE,
                                value: 10,
                            },
                        },
                    },
                ]),
            )
        })

        it('collapses multiple eq on same custom property into MATCHES_ALL_OF', () => {
            const filters = {
                and: [
                    { custom_tags: { [ConditionOperatorType.EQUALS]: 'tag_a' } },
                    { custom_tags: { [ConditionOperatorType.EQUALS]: 'tag_b' } },
                    { custom_tags: { [ConditionOperatorType.EQUALS]: 'tag_c' } },
                ],
            } as any

            const result = extractFilterRecordMapFromPQuery(filters)
            const values = Array.from(result.included.values())

            expect(values).toEqual([
                {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: 'custom_tags',
                    nestedFilter: {
                        filterType: FilterType.MATCHES_ALL_OF,
                        value: ['tag_a', 'tag_b', 'tag_c'],
                    },
                },
            ])
        })

        it('collapses multiple neq on same custom property into excluded MATCHES_ALL_OF', () => {
            const filters = {
                and: [
                    { content_type: { [ConditionOperatorType.NOT_EQUALS]: 'sponsored' } },
                    { content_type: { [ConditionOperatorType.NOT_EQUALS]: 'advertorial' } },
                ],
            } as any

            const result = extractFilterRecordMapFromPQuery(filters)
            const excludedValues = Array.from(result.excluded.values())

            expect(excludedValues).toEqual([
                {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: 'content_type',
                    nestedFilter: {
                        filterType: FilterType.MATCHES_ALL_OF,
                        value: ['sponsored', 'advertorial'],
                    },
                },
            ])
        })

        it('handles custom property NEQ_ANY as MATCHES_ANY_OF in excluded', () => {
            const filters = {
                and: [{ content_type: { [ConditionOperatorType.NEQ_ANY]: ['sponsored', 'native_ad'] } }],
            } as any

            const result = extractFilterRecordMapFromPQuery(filters)
            const excludedValues = Array.from(result.excluded.values())

            expect(excludedValues).toEqual([
                {
                    blueConicType: BlueConicFilterType.CUSTOM,
                    filterType: FilterType.CUSTOM,
                    property: 'content_type',
                    nestedFilter: {
                        filterType: FilterType.MATCHES_ANY_OF,
                        value: ['sponsored', 'native_ad'],
                    },
                },
            ])
        })

        it('handles custom property nested date filter as SPECIFIC_DATE', () => {
            const filters = {
                and: [
                    {
                        custom_date: {
                            [ConditionOperatorType.LT]: '2024-01-01',
                            meta: { timezone: 'UTC' },
                        },
                    },
                ],
            } as any

            const result = extractFilterRecordMapFromPQuery(filters)
            const values = Array.from(result.included.values())

            const custom = values.find((v: any) => (v as any).property === 'custom_date') as any
            expect(custom.blueConicType).toBe(BlueConicFilterType.CUSTOM)
            expect(custom.filterType).toBe(FilterType.CUSTOM)
            expect(custom.nestedFilter.filterType).toBe(FilterType.SPECIFIC_DATE)
            expect(custom.nestedFilter.value).toEqual({
                operator: ConditionOperatorType.LT,
                value: '2024-01-01',
                meta: { timezone: 'UTC' },
            })
        })

        it('handles custom property relative date filter as RELATIVE_DATE', () => {
            const filters = {
                and: [
                    {
                        custom_age: {
                            [ConditionOperatorType.LTE]: 30,
                            meta: { timezone: 'America/Chicago', qualifier: 'days' },
                        },
                    },
                ],
            } as any

            const result = extractFilterRecordMapFromPQuery(filters)
            const values = Array.from(result.included.values())

            const custom = values.find((v: any) => (v as any).property === 'custom_age') as any
            expect(custom.blueConicType).toBe(BlueConicFilterType.CUSTOM)
            expect(custom.filterType).toBe(FilterType.CUSTOM)
            expect(custom.nestedFilter.filterType).toBe(FilterType.RELATIVE_DATE)
            expect(custom.nestedFilter.value).toEqual({
                operator: ConditionOperatorType.LTE,
                value: 30,
                meta: { timezone: 'America/Chicago', qualifier: 'days' },
            })
        })
    })

    describe('parsePQueryFromFilterRecordMap', () => {
        it('returns null for empty record map', () => {
            const recordMap = {
                included: new Map(),
                excluded: new Map(),
                products: [],
            }

            const result = parsePQueryFromFilterRecordMap(recordMap)
            expect(result).toEqual(null)
        })

        it('maps product filters to document', () => {
            const recordMap = {
                included: new Map(),
                excluded: new Map(),
                products: [ProductsFilters.BOUGHT, ProductsFilters.IN_STOCK],
            }

            const result = parsePQueryFromFilterRecordMap(recordMap)
            expect(result).toEqual({
                and: [
                    { product_filter: { [ConditionOperatorType.EQUALS]: ProductsFilters.BOUGHT } },
                    { product_filter: { [ConditionOperatorType.EQUALS]: ProductsFilters.IN_STOCK } },
                ],
            })
        })

        it('maps included any-of and all-of and free-text', () => {
            const recordMap = {
                included: new Map([
                    [
                        '1',
                        {
                            blueConicType: BlueConicFilterType.CATEGORY,
                            filterType: FilterType.MATCHES_ANY_OF,
                            value: ['cat1', 'cat2'],
                        } as Filter,
                    ],
                    [
                        '2',
                        {
                            blueConicType: BlueConicFilterType.AUTHOR,
                            filterType: FilterType.MATCHES_ALL_OF,
                            value: ['a1'],
                        } as Filter,
                    ],
                    [
                        '3',
                        {
                            blueConicType: BlueConicFilterType.PROPERTY,
                            filterType: FilterType.FREE_TEXT,
                            value: 'site-x',
                        } as Filter,
                    ],
                ]),
                excluded: new Map(),
                products: [],
            }

            const result = parsePQueryFromFilterRecordMap(recordMap)
            expect(result).toEqual({
                and: [
                    { [BlueConicFilterType.CATEGORY]: { [ConditionOperatorType.EQ_ANY]: ['cat1', 'cat2'] } },
                    { [BlueConicFilterType.AUTHOR]: { [ConditionOperatorType.EQUALS]: 'a1' } },
                    { [BlueConicFilterType.PROPERTY]: { [ConditionOperatorType.EQUALS]: 'site-x' } },
                ],
            })
        })

        it('maps excluded any-of, all-of (as multiple not_equals), and free-text', () => {
            const recordMap = {
                included: new Map(),
                excluded: new Map([
                    [
                        '1',
                        {
                            blueConicType: BlueConicFilterType.CATEGORY,
                            filterType: FilterType.MATCHES_ANY_OF,
                            value: ['cat1', 'cat2'],
                        } as Filter,
                    ],
                    [
                        '2',
                        {
                            blueConicType: BlueConicFilterType.AUTHOR,
                            filterType: FilterType.MATCHES_ALL_OF,
                            value: ['a1', 'a2'],
                        } as Filter,
                    ],
                    [
                        '3',
                        {
                            blueConicType: BlueConicFilterType.PROPERTY,
                            filterType: FilterType.FREE_TEXT,
                            value: 'site-x',
                        } as Filter,
                    ],
                ]),
                products: [],
            }

            const result = parsePQueryFromFilterRecordMap(recordMap)
            expect(result).toEqual({
                and: [
                    { [BlueConicFilterType.CATEGORY]: { [ConditionOperatorType.NEQ_ANY]: ['cat1', 'cat2'] } },
                    { [BlueConicFilterType.AUTHOR]: { [ConditionOperatorType.NOT_EQUALS]: 'a1' } },
                    { [BlueConicFilterType.AUTHOR]: { [ConditionOperatorType.NOT_EQUALS]: 'a2' } },
                    { [BlueConicFilterType.PROPERTY]: { [ConditionOperatorType.NOT_EQUALS]: 'site-x' } },
                ],
            })
        })

        it('maps specific date with meta and excluded inversion', () => {
            const recordMap = {
                included: new Map([
                    [
                        '1',
                        {
                            blueConicType: BlueConicFilterType.PUBLICATION_DATE,
                            filterType: FilterType.SPECIFIC_DATE,
                            value: {
                                operator: ConditionOperatorType.EQUALS,
                                value: '2025-08-19',
                                meta: { timezone: 'America/Chicago' },
                            },
                        } as any,
                    ],
                ]),
                excluded: new Map([
                    [
                        '2',
                        {
                            blueConicType: BlueConicFilterType.PUBLICATION_DATE,
                            filterType: FilterType.SPECIFIC_DATE,
                            value: {
                                operator: ConditionOperatorType.EQUALS,
                                value: '2025-01-01',
                                meta: { timezone: 'America/Chicago' },
                            },
                        } as any,
                    ],
                ]),
                products: [],
            }

            const result = parsePQueryFromFilterRecordMap(recordMap)
            expect(result).toEqual({
                and: [
                    {
                        [BlueConicFilterType.PUBLICATION_DATE]: {
                            [ConditionOperatorType.EQUALS]: '2025-08-19',
                            meta: { timezone: 'America/Chicago' },
                        },
                    },
                    {
                        [BlueConicFilterType.PUBLICATION_DATE]: {
                            [ConditionOperatorType.NOT_EQUALS]: '2025-01-01',
                            meta: { timezone: 'America/Chicago' },
                        },
                    },
                ],
            })
        })

        it('maps custom nested numerical and nested date with operator inversion on exclusion', () => {
            const included = new Map<string, any>([
                [
                    'n',
                    {
                        blueConicType: BlueConicFilterType.CUSTOM,
                        filterType: FilterType.CUSTOM,
                        property: 'score',
                        nestedFilter: {
                            filterType: FilterType.NUMERICAL,
                            value: { operator: ConditionOperatorType.GT, value: 5 },
                        },
                    },
                ],
                [
                    'd',
                    {
                        blueConicType: BlueConicFilterType.CUSTOM,
                        filterType: FilterType.CUSTOM,
                        property: 'some_date',
                        nestedFilter: {
                            filterType: FilterType.SPECIFIC_DATE,
                            value: { operator: ConditionOperatorType.EQUALS, value: '2024-12-31', meta: { tz: 'UTC' } },
                        },
                    },
                ],
            ])
            const excluded = new Map<string, any>([
                [
                    'n2',
                    {
                        blueConicType: BlueConicFilterType.CUSTOM,
                        filterType: FilterType.CUSTOM,
                        property: 'score',
                        nestedFilter: {
                            filterType: FilterType.NUMERICAL,
                            value: { operator: ConditionOperatorType.LTE, value: 10 },
                        },
                    },
                ],
                [
                    'd2',
                    {
                        blueConicType: BlueConicFilterType.CUSTOM,
                        filterType: FilterType.CUSTOM,
                        property: 'some_date',
                        nestedFilter: {
                            filterType: FilterType.RELATIVE_DATE,
                            value: { operator: ConditionOperatorType.GTE, value: 30, meta: { qualifier: 'days' } },
                        },
                    },
                ],
            ])
            const recordMap = { included, excluded, products: [] }

            const result = parsePQueryFromFilterRecordMap(recordMap)
            expect(result).toEqual({
                and: [
                    { score: { [ConditionOperatorType.GT]: 5 } },
                    { some_date: { [ConditionOperatorType.EQUALS]: '2024-12-31', meta: { tz: 'UTC' } } },
                    { score: { [ConditionOperatorType.GTE]: 10 } },
                    { some_date: { [ConditionOperatorType.LTE]: 30, meta: { qualifier: 'days' } } },
                ],
            })
        })
    })

    describe('operator mapping helpers', () => {
        it('getPQueryConditionFromFilterType maps as expected', () => {
            expect(getPQueryConditionFromFilterType(FilterType.FREE_TEXT, false)).toBe(ConditionOperatorType.EQUALS)
            expect(getPQueryConditionFromFilterType(FilterType.FREE_TEXT, true)).toBe(ConditionOperatorType.NOT_EQUALS)
            expect(getPQueryConditionFromFilterType(FilterType.MATCHES_ALL_OF, false)).toBe(
                ConditionOperatorType.EQUALS,
            )
            expect(getPQueryConditionFromFilterType(FilterType.MATCHES_ALL_OF, true)).toBe(
                ConditionOperatorType.NOT_EQUALS,
            )
            expect(getPQueryConditionFromFilterType(FilterType.MATCHES_ANY_OF, false)).toBe(
                ConditionOperatorType.EQ_ANY,
            )
            expect(getPQueryConditionFromFilterType(FilterType.MATCHES_ANY_OF, true)).toBe(
                ConditionOperatorType.NEQ_ANY,
            )
            expect(getPQueryConditionFromFilterType(FilterType.SPECIFIC_DATE, false)).toBe(ConditionOperatorType.EQUALS)
            expect(getPQueryConditionFromFilterType(FilterType.SPECIFIC_DATE, true)).toBe(
                ConditionOperatorType.NOT_EQUALS,
            )
        })

        it('getPQueryConditionFromConditionContext handles inversion correctly', () => {
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.EQUALS, true)).toBe(
                ConditionOperatorType.NOT_EQUALS,
            )
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.EQUALS, false)).toBe(
                ConditionOperatorType.EQUALS,
            )
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.LT, true)).toBe(
                ConditionOperatorType.GT,
            )
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.LTE, true)).toBe(
                ConditionOperatorType.GTE,
            )
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.GT, true)).toBe(
                ConditionOperatorType.LT,
            )
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.GTE, true)).toBe(
                ConditionOperatorType.LTE,
            )
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.EQ_ANY, true)).toBe(
                ConditionOperatorType.NEQ_ANY,
            )
            expect(getPQueryConditionFromConditionContext(ConditionOperatorType.EQ_ANY, false)).toBe(
                ConditionOperatorType.EQ_ANY,
            )
        })
    })

    describe('simpleDateFormat', () => {
        it('formats with leading zeros for month/day', () => {
            const d = new Date('2023-03-05T12:00:00Z')
            expect(simpleDateFormat(d)).toBe('2023-03-05')
        })
        it('formats double-digit month/day', () => {
            const d = new Date('2021-12-31T12:00:00Z')
            expect(simpleDateFormat(d)).toBe('2021-12-31')
        })
    })
}
